/**
 * Animated Counter Component
 * Counts up from 0 to target value with formatting
 */

import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

interface AnimatedCounterProps {
  target: number;
  duration?: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  className?: string;
  animate?: boolean;
  formatAsCurrency?: boolean;
  formatAsTrillions?: boolean;
}

export function AnimatedCounter({
  target,
  duration = 2,
  prefix = '',
  suffix = '',
  decimals = 0,
  className = '',
  animate = true,
  formatAsCurrency = false,
  formatAsTrillions = false,
}: AnimatedCounterProps) {
  const counterRef = useRef<HTMLSpanElement>(null);
  const [displayValue, setDisplayValue] = useState('0');
  const valueRef = useRef({ value: 0 });
  
  useEffect(() => {
    if (!animate || !counterRef.current) {
      // Set final value immediately if not animating
      setDisplayValue(formatValue(target));
      return;
    }
    
    valueRef.current.value = 0;
    
    gsap.to(valueRef.current, {
      value: target,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        setDisplayValue(formatValue(valueRef.current.value));
      },
    });
    
    return () => {
      gsap.killTweensOf(valueRef.current);
    };
  }, [target, duration, animate]);
  
  function formatValue(value: number): string {
    if (formatAsTrillions) {
      const trillions = value / 1e12;
      return trillions.toFixed(decimals);
    }
    
    if (formatAsCurrency) {
      if (value >= 1e12) {
        return (value / 1e12).toFixed(decimals);
      }
      if (value >= 1e9) {
        return (value / 1e9).toFixed(decimals);
      }
      if (value >= 1e6) {
        return (value / 1e6).toFixed(decimals);
      }
    }
    
    return value.toFixed(decimals);
  }
  
  return (
    <span ref={counterRef} className={className}>
      {prefix}{displayValue}{suffix}
    </span>
  );
}

interface AnimatedPercentageProps {
  target: number;
  duration?: number;
  decimals?: number;
  className?: string;
  animate?: boolean;
}

export function AnimatedPercentage({
  target,
  duration = 1.5,
  decimals = 1,
  className = '',
  animate = true,
}: AnimatedPercentageProps) {
  const [displayValue, setDisplayValue] = useState('0');
  const valueRef = useRef({ value: 0 });
  
  useEffect(() => {
    if (!animate) {
      setDisplayValue(target.toFixed(decimals));
      return;
    }
    
    valueRef.current.value = 0;
    
    gsap.to(valueRef.current, {
      value: target,
      duration,
      ease: 'power2.out',
      onUpdate: () => {
        setDisplayValue(valueRef.current.value.toFixed(decimals));
      },
    });
    
    return () => {
      gsap.killTweensOf(valueRef.current);
    };
  }, [target, duration, decimals, animate]);
  
  return (
    <span className={className}>
      {displayValue}%
    </span>
  );
}

export default AnimatedCounter;

